import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Tooltip,
  Popper,
  ClickAwayListener,
} from "@mui/material";
import {
  buildTimelineMonthEndMarkers,
  formatMonthLabel,
  getInteractionCountByMonth,
  getMonthlyScoreColor,
  getMonthlyScoreLabel,
  getSentimentColor,
  getTimelinePositionPercent,
  groupTimelineItemsByDate,
  TimelineItem,
  TimelineTooltipPayload,
} from "../../utils/utils";
import { InteractionsTimelineProps, TimelineMarker } from "../../utils/types";
import { useLocation } from "react-router-dom";
import InteractionTooltip from "../Accounts/interactionTooltip";
import EmailTooltip from "../Accounts/EmailTooltip";
import CombinedTooltip from "../Accounts/CombinedTooltip";
import "../../styles/interactionsTimeline.css";

const InteractionsTimeline: React.FC<InteractionsTimelineProps> = ({
  items = [],
  startDate,
  endDate,
  mode,
  showInteractions,
  showEmails,
  showLaneLabels,
  showDateRow = true,
  showMonthEndMarkers = true,
  monthlyScore = null,
  navigationState,
  onTimelineClick,
  isSlideOverOpen,
  className,
  sx,
}) => {
  // Determine active lanes
  const isInteractionsActive = useMemo(() => {
    if (mode === "interactions") return true;
    if (mode === "emails") return false;
    if (mode === "both") return true;
    if (typeof showInteractions === "boolean") return showInteractions;
    return true; // default
  }, [mode, showInteractions]);

  const isEmailsActive = useMemo(() => {
    if (mode === "emails") return true;
    if (mode === "interactions") return false;
    if (mode === "both") return true;
    if (typeof showEmails === "boolean") return showEmails;
    return mode === "both";
  }, [mode, showEmails]);

  const shouldShowLaneLabels = useMemo(() => {
    if (typeof showLaneLabels === "boolean") return showLaneLabels;
    return isInteractionsActive && isEmailsActive;
  }, [showLaneLabels, isInteractionsActive, isEmailsActive]);

  // Tooltip & hover state
  const location = useLocation();
  const [restoredLocationKey, setRestoredLocationKey] = useState<string | null>(null);
  const timelineWrapRef = useRef<HTMLDivElement | null>(null);
  const [timelineAnchorEl, setTimelineAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [activeTimelineItem, setActiveTimelineItem] =
    useState<TimelineTooltipPayload | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [hoveredTooltipId, setHoveredTooltipId] = useState<string | null>(null);
  const timelineTooltipContentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (isSlideOverOpen === false) {
      setTimelineAnchorEl(null);
      setActiveTimelineItem(null);
    }
  }, [isSlideOverOpen]);

  // Normalize dates to full day boundaries
  const timelineStartDate = useMemo(() => {
    const d = new Date(startDate);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [startDate]);

  const timelineEndDate = useMemo(() => {
    const d = new Date(endDate);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [endDate]);

  const getTimelinePosition = (dateValue: string | Date) => {
    return getTimelinePositionPercent(
      new Date(dateValue),
      timelineStartDate,
      timelineEndDate,
    );
  };

  // Month-end trend markers (flags)
  const monthEndMarkers = useMemo(() => {
    if (!showMonthEndMarkers) return [];
    return buildTimelineMonthEndMarkers(
      monthlyScore,
      timelineStartDate,
      timelineEndDate,
    );
  }, [showMonthEndMarkers, monthlyScore, timelineEndDate, timelineStartDate]);

  // Date markers (ruler ticks along the date range)
  const timelineMarkers = useMemo(() => {
    const markers: TimelineMarker[] = [];
    if (
      !timelineStartDate ||
      !timelineEndDate ||
      Number.isNaN(timelineStartDate.getTime()) ||
      Number.isNaN(timelineEndDate.getTime())
    ) {
      return markers;
    }

    const formatMonthEndLabel = (d: Date) =>
      d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

    markers.push({
      label: formatMonthEndLabel(timelineStartDate),
      date: timelineStartDate,
      align: "left",
    });

    let cursorMonth = new Date(
      timelineStartDate.getFullYear(),
      timelineStartDate.getMonth(),
      1,
    );

    while (cursorMonth <= timelineEndDate) {
      const year = cursorMonth.getFullYear();
      const month = cursorMonth.getMonth() + 1;
      const monthEndDate = new Date(year, month, 0, 23, 59, 59, 999);

      if (
        monthEndDate >= timelineStartDate &&
        monthEndDate <= timelineEndDate
      ) {
        markers.push({
          label: formatMonthEndLabel(monthEndDate),
          date: monthEndDate,
          align: "center",
        });
      }

      cursorMonth.setMonth(cursorMonth.getMonth() + 1);
    }

    const lastMonthMarker = markers[markers.length - 1];
    const isEndAlreadyPresent =
      lastMonthMarker &&
      lastMonthMarker.date.getFullYear() === timelineEndDate.getFullYear() &&
      lastMonthMarker.date.getMonth() === timelineEndDate.getMonth() &&
      lastMonthMarker.date.getDate() === timelineEndDate.getDate();

    if (!isEndAlreadyPresent) {
      if (lastMonthMarker) {
        const lastMonthPosition = getTimelinePosition(lastMonthMarker.date);
        const endPosition = getTimelinePosition(timelineEndDate);

        if (endPosition - lastMonthPosition < 8) {
          lastMonthMarker.align = "right";
        }
      }

      markers.push({
        label: formatMonthEndLabel(timelineEndDate),
        date: timelineEndDate,
        align: "right",
      });
    }

    return markers;
  }, [timelineStartDate, timelineEndDate]);

  // Grouped timeline items by date
  const groupedTimelineItems = useMemo(
    () => groupTimelineItemsByDate(items, true),
    [items],
  );

  const interactionCountByMonth = useMemo(
    () => getInteractionCountByMonth(items),
    [items],
  );

  const getTimelineDotStyles = (item: TimelineItem) => {
    const isHovered = hoveredId === item.id;
    const isActive = activeTimelineItem?.interactions.some(
      (interaction) => interaction.id === item.id,
    );
    const dotSize = item.type === "interaction" ? 14 : 8;
    const currentMonthSentimentColor =
      item.type === "interaction"
        ? getSentimentColor(item.overallSentiment || null)
        : null;

    return {
      left: `${getTimelinePosition(item.start_time)}%`,
      width:
        isActive || isHovered
          ? dotSize + (item.type === "interaction" ? 4 : 2)
          : dotSize,
      height:
        isActive || isHovered
          ? dotSize + (item.type === "interaction" ? 4 : 2)
          : dotSize,
      border:
        item.type === "interaction" ? "1px solid #EFEBFB" : "1px solid #805EDE",
      backgroundColor: currentMonthSentimentColor
        ? currentMonthSentimentColor
        : isActive
          ? item.type === "interaction"
            ? "#6036d6"
            : "#B6A3EC"
          : isHovered
            ? item.type === "interaction"
              ? "#7C5AE0"
              : "#CEC1F2"
            : item.type === "interaction"
              ? "#9478e4"
              : "#EFEBFB",
      boxShadow:
        item.type === "interaction"
          ? isActive || isHovered
            ? "0 0 0 1px white"
            : "none"
          : isActive
            ? "0px 2px 4px 0px #00000040"
            : "none",
      zIndex: isActive ? 3 : 2,
    };
  };

  const getDotCountTooltip = (groupItems: TimelineItem[]) => {
    const interactionCount = groupItems.filter(
      (i) => i.type === "interaction",
    ).length;
    const emailCount = groupItems.filter((i) => i.type === "email").length;
    const parts: string[] = [];

    if (interactionCount) {
      parts.push(
        `${interactionCount} Interaction${interactionCount > 1 ? "s" : ""}`,
      );
    }
    if (emailCount) {
      parts.push(`${emailCount} Email${emailCount > 1 ? "s" : ""}`);
    }

    return parts.join(" & ");
  };

  const isActiveDetailDot = (groupItems: TimelineItem[]) => {
    if (!timelineAnchorEl || !activeTimelineItem) return false;

    const activeIds = new Set(
      activeTimelineItem.interactions.map((item) => item.id),
    );

    return (
      groupItems.length === activeTimelineItem.interactions.length &&
      groupItems.every((item) => activeIds.has(item.id))
    );
  };

  const handleTimelineDotClick = (
    e: React.MouseEvent<HTMLElement>,
    groupItems: TimelineItem[],
    groupDateKey: string,
    anchorInteractionId: string,
  ) => {
    setHoveredTooltipId(null);

    if (
      timelineAnchorEl === e.currentTarget &&
      activeTimelineItem &&
      activeTimelineItem.interactions.length === groupItems.length &&
      activeTimelineItem.interactions.every(
        (item, idx) => item.id === groupItems[idx].id,
      )
    ) {
      return;
    }

    onTimelineClick?.();

    const hasInteractions = groupItems.some(
      (item) => item.type === "interaction",
    );
    const hasEmails = groupItems.some((item) => item.type === "email");
    const isMixed = hasInteractions && hasEmails;

    setTimelineAnchorEl(e.currentTarget);
    setActiveTimelineItem({
      mode: isMixed ? "mixed" : groupItems.length > 1 ? "group" : "single",
      interactions: groupItems,
      emailIds: groupItems
        .filter((item) => item.type === "email")
        .map((item) => item.id)
        .filter(Boolean),
      navigationState,
      groupDateKey,
      anchorInteractionId,
    });
  };

  // Restore tooltip when navigating back from IDP (Interaction Details Page)
  useEffect(() => {
    if (restoredLocationKey === location.key) return;
    if (!location.state?.restoreTooltip) return;
    if (isSlideOverOpen !== undefined) {
      if (!isSlideOverOpen) return;
    }
    if (!location.state?.interactionId) return;
    if (!items.length) return;

    const clickedId = location.state?.interactionId;
    const hasInteraction = items.some((item) => item.id === clickedId);
    if (!hasInteraction) return;

    const tooltipPayload = location.state
      ?.tooltipPayload as TimelineTooltipPayload | undefined;
    const groupDateKey =
      location.state?.tooltipGroupDateKey || tooltipPayload?.groupDateKey;
    const anchorInteractionId =
      location.state?.tooltipAnchorInteractionId ||
      tooltipPayload?.anchorInteractionId ||
      clickedId;

    let tooltipItems =
      tooltipPayload?.interactions?.filter((item) =>
        items.some((timelineItem) => timelineItem.id === item.id),
      ) || [];

    if (!tooltipItems.length && groupDateKey) {
      const matchingGroup = groupedTimelineItems.find(
        (group) => group.dateKey === groupDateKey,
      );
      if (matchingGroup) {
        tooltipItems = matchingGroup.items;
      }
    }

    if (!tooltipItems.length) {
      const clickedInteraction = items.find((item) => item.id === clickedId);
      if (!clickedInteraction) return;

      const clickedDate = new Date(
        clickedInteraction.start_time,
      ).toDateString();
      tooltipItems = items.filter(
        (item) =>
          item.type === clickedInteraction.type &&
          new Date(item.start_time).toDateString() === clickedDate,
      );
    }

    let attempts = 0;
    const attemptRestore = () => {
      const anchorElement = timelineWrapRef.current?.querySelector(
        `[data-interaction-id="${anchorInteractionId}"]`,
      ) as HTMLElement | null;

      if (!anchorElement) {
        if (attempts < 10) {
          attempts++;
          setTimeout(attemptRestore, 50);
        }
        return;
      }

      setTimelineAnchorEl(anchorElement);
      const hasInteractions = tooltipItems.some(
        (item) => item.type === "interaction",
      );
      const hasEmails = tooltipItems.some((item) => item.type === "email");
      setActiveTimelineItem({
        mode:
          hasInteractions && hasEmails
            ? "mixed"
            : tooltipItems.length > 1
              ? "group"
              : "single",
        interactions: tooltipItems,
        emailIds: tooltipItems
          .filter((item) => item.type === "email")
          .map((item) => item.id)
          .filter(Boolean),
        navigationState,
        groupDateKey:
          groupDateKey ||
          `${new Date(tooltipItems[0]?.start_time).toISOString().split("T")[0]}-${tooltipItems[0]?.type}`,
        anchorInteractionId,
      });
      setRestoredLocationKey(location.key);
    };

    attemptRestore();
  }, [
    groupedTimelineItems,
    location.key,
    location.state,
    restoredLocationKey,
    items,
    navigationState,
    isSlideOverOpen,
  ]);

  useEffect(() => {
    if (!timelineAnchorEl || !activeTimelineItem) return;

    const rect = timelineAnchorEl.getBoundingClientRect();
    const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

    if (isVisible) return;

    requestAnimationFrame(() => {
      timelineAnchorEl.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    });
  }, [timelineAnchorEl, activeTimelineItem]);

  return (
    <>
      <Box
        ref={timelineWrapRef}
        className={`interactions-timeline-wrap ${className || ""}`.trim()}
        sx={sx}
      >
        {/* Optional Lane Labels */}
        {shouldShowLaneLabels && (
          <Box className="interactions-timeline-lane-labels">
            {isInteractionsActive && (
              <Box className="interactions-timeline-lane-label-row">
                <Typography className="interactions-timeline-lane-label">
                  Interactions :
                </Typography>
              </Box>
            )}
            {showDateRow && isInteractionsActive && isEmailsActive && (
              <Box className="interactions-timeline-date-row" />
            )}
            {isEmailsActive && (
              <Box className="interactions-timeline-lane-label-row">
                <Typography className="interactions-timeline-lane-label">
                  Emails :
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Timeline Tracks */}
        <Box className="interactions-timeline-track">
          {/* Lane 1: Interactions */}
          {isInteractionsActive && (
            <Box className="interactions-timeline-lane">
              <Box className="interactions-timeline-line" />

              {/* Month-End Trend Markers (Flags) */}
              {monthEndMarkers.map((marker) => {
                const count = interactionCountByMonth[marker.monthKey] || 0;
                const scoreLabel = getMonthlyScoreLabel(
                  monthlyScore?.[marker.monthKey],
                );
                const scoreColor = getMonthlyScoreColor(scoreLabel);

                return (
                  <Box
                    key={marker.monthKey}
                    className="interactions-timeline-flag-box"
                    sx={{
                      left: `${marker.position}%`,
                      transform: "translate(-20%, -50%)",
                    }}
                  >
                    <Tooltip
                      title={
                        <Box className="flags-tooltip-content">
                          <Box sx={{ gap: "5px", display: "flex" }}>
                            <Typography className="flags-date-header">
                              {formatMonthLabel(marker.monthKey)} Trend:
                            </Typography>
                            <Typography>
                              <Box
                                component="span"
                                sx={{ color: scoreColor, fontWeight: 600 }}
                              >
                                {scoreLabel}
                              </Box>
                            </Typography>
                          </Box>
                          <Typography className="flags-interaction-count">
                            Interactions: {count}
                          </Typography>
                        </Box>
                      }
                      placement="bottom-start"
                      classes={{ popper: "flags-tooltip" }}
                    >
                      <Box
                        className="monthly-trend-flag-icon"
                        component="span"
                        sx={{
                          display: "block",
                          transition: "transform 0.18s ease-in-out",
                        }}
                      >
                        <img
                          src={marker.icon}
                          alt="monthly trend"
                          className="interactions-timeline-flag-icon"
                        />
                      </Box>
                    </Tooltip>
                  </Box>
                );
              })}

              {/* Interaction Dots */}
              {groupedTimelineItems
                .filter((group) => group.items[0]?.type === "interaction")
                .map((group) => {
                  const representative = group.items[0];
                  const dotCountTooltip = getDotCountTooltip(group.items);
                  const showHoverCountTooltip =
                    hoveredTooltipId === representative.id &&
                    !isActiveDetailDot(group.items);
                  const dotProps = {
                    "data-timeline-dot": "true",
                    "data-interaction-id": representative.id,
                    onMouseEnter: () => {
                      setHoveredId(representative.id);
                      setHoveredTooltipId(representative.id);
                    },
                    onMouseLeave: () => {
                      setHoveredId(null);
                      setHoveredTooltipId(null);
                    },
                    onClick: (e: React.MouseEvent<HTMLElement>) =>
                      handleTimelineDotClick(
                        e,
                        group.items,
                        group.dateKey,
                        representative.id,
                      ),
                  };

                  return (
                    <Tooltip
                      key={group.dateKey}
                      title={dotCountTooltip}
                      placement="top"
                      arrow
                      disableInteractive
                      classes={{ popper: "tooltip-count" }}
                      open={showHoverCountTooltip}
                    >
                      <Box
                        {...dotProps}
                        className="interactions-timeline-dot"
                        sx={getTimelineDotStyles(representative)}
                      />
                    </Tooltip>
                  );
                })}

              {/* Stems to Date Row */}
              {showDateRow &&
                timelineMarkers.map((marker, idx) => {
                  const position = getTimelinePosition(marker.date);
                  const stemTransform =
                    marker.align === "left"
                      ? "translateX(0)"
                      : marker.align === "right"
                        ? "translateX(-100%)"
                        : "translateX(-50%)";
                  return (
                    <Box
                      key={`interaction-stem-${idx}`}
                      className="interactions-timeline-interaction-stem"
                      sx={{
                        left: `${position}%`,
                        transform: stemTransform,
                      }}
                    />
                  );
                })}
            </Box>
          )}

          {/* Date Row Markers */}
          {showDateRow && (
            <Box className="interactions-timeline-date-row">
              {timelineMarkers.map((marker, idx) => {
                const position = getTimelinePosition(marker.date);
                const containerTransform =
                  marker.align === "left"
                    ? "translateX(0)"
                    : marker.align === "right"
                      ? "translateX(-100%)"
                      : "translateX(-50%)";

                return (
                  <Box
                    key={`${marker.label}-${idx}`}
                    className="interactions-timeline-date-marker-column"
                    sx={{
                      left: `${position}%`,
                      transform: containerTransform,
                      alignItems:
                        marker.align === "left"
                          ? "flex-start"
                          : marker.align === "right"
                            ? "flex-end"
                            : "center",
                    }}
                  >
                    {isInteractionsActive && (
                      <Box className="interactions-timeline-date-connector" />
                    )}
                    <Typography className="interactions-timeline-date-label">
                      {marker.label}
                    </Typography>
                    {isEmailsActive && (
                      <Box className="interactions-timeline-date-connector" />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Lane 2: Emails */}
          {isEmailsActive && (
            <Box className="interactions-timeline-lane">
              <Box className="interactions-timeline-line" />

              {/* Email Dots */}
              {groupedTimelineItems
                .filter((group) => group.items[0]?.type === "email")
                .map((group) => {
                  const representative = group.items[0];
                  const dotCountTooltip = getDotCountTooltip(group.items);
                  const showHoverCountTooltip =
                    hoveredTooltipId === representative.id &&
                    !isActiveDetailDot(group.items);
                  const dotProps = {
                    "data-timeline-dot": "true",
                    "data-interaction-id": representative.id,
                    onMouseEnter: () => {
                      setHoveredId(representative.id);
                      setHoveredTooltipId(representative.id);
                    },
                    onMouseLeave: () => {
                      setHoveredId(null);
                      setHoveredTooltipId(null);
                    },
                    onClick: (e: React.MouseEvent<HTMLElement>) =>
                      handleTimelineDotClick(
                        e,
                        group.items,
                        group.dateKey,
                        representative.id,
                      ),
                  };

                  return (
                    <Tooltip
                      key={group.dateKey}
                      title={dotCountTooltip}
                      placement="top"
                      arrow
                      disableInteractive
                      classes={{ popper: "tooltip-count" }}
                      open={showHoverCountTooltip}
                    >
                      <Box
                        {...dotProps}
                        className="interactions-timeline-dot"
                        sx={getTimelineDotStyles(representative)}
                      />
                    </Tooltip>
                  );
                })}

              {/* Stems to Date Row */}
              {showDateRow &&
                timelineMarkers.map((marker, idx) => {
                  const position = getTimelinePosition(marker.date);
                  const stemTransform =
                    marker.align === "left"
                      ? "translateX(0)"
                      : marker.align === "right"
                        ? "translateX(-100%)"
                        : "translateX(-50%)";
                  return (
                    <Box
                      key={`email-stem-${idx}`}
                      className="interactions-timeline-email-stem"
                      sx={{
                        left: `${position}%`,
                        transform: stemTransform,
                      }}
                    />
                  );
                })}
            </Box>
          )}
        </Box>
      </Box>

      {/* Popover for clicked timeline interaction/email */}
      {/* Popper for clicked timeline interaction/email */}
      <Popper
        open={Boolean(timelineAnchorEl)}
        anchorEl={timelineAnchorEl}
        placement="bottom-start"
        popperOptions={{
          strategy: "fixed",
        }}
        sx={{
          zIndex: 2000,
        }}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 8],
            },
          },
          {
            name: "flip",
            enabled: true,
            options: {
              altBoundary: true,
              rootBoundary: "viewport",
              fallbackPlacements: ["top-start", "top-end", "bottom-end"],
            },
          },
          {
            name: "preventOverflow",
            options: {
              altAxis: true,
              altBoundary: true,
              rootBoundary: "viewport",
              boundary: "viewport",
              padding: 8,
            },
          },
        ]}
      >
        <ClickAwayListener
          onClickAway={(event) => {
            const target = event.target as HTMLElement;
            if (target.closest('[data-timeline-dot="true"]')) {
              return;
            }
            setTimelineAnchorEl(null);
            setActiveTimelineItem(null);
          }}
        >
          <Box
            ref={timelineTooltipContentRef}
            className="interactions-timeline-popover-content"
            sx={{
              borderRadius: "8px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
            }}
          >
            {activeTimelineItem &&
              (activeTimelineItem.mode === "mixed" ? (
                <CombinedTooltip
                  mode={activeTimelineItem.mode}
                  interactions={activeTimelineItem.interactions}
                  emailIds={activeTimelineItem.emailIds}
                  navigationState={activeTimelineItem.navigationState}
                  groupDateKey={activeTimelineItem.groupDateKey}
                  anchorInteractionId={activeTimelineItem.anchorInteractionId}
                />
              ) : activeTimelineItem.interactions[0]?.type === "email" ? (
                <EmailTooltip
                  mode={activeTimelineItem.mode}
                  interactions={activeTimelineItem.interactions}
                  emailIds={activeTimelineItem.emailIds}
                />
              ) : (
                <InteractionTooltip
                  mode={activeTimelineItem.mode}
                  interactions={activeTimelineItem.interactions}
                  navigationState={activeTimelineItem.navigationState}
                  groupDateKey={activeTimelineItem.groupDateKey}
                  anchorInteractionId={activeTimelineItem.anchorInteractionId}
                />
              ))}
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default InteractionsTimeline;
